"use client";

import React, { useCallback, useState, useEffect, useMemo } from "react";
import { QuickDealCreationModal } from "@/features/deals/components/QuickDealCreationModal";
import { useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import {
  useDealsPaginated,
  type DealListItem,
} from "@/features/deals/hooks/use-deals-paginated";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import type { SelectOption } from "@/components/ui/responsive-select";
import { EntitySelectShell } from "./shared/EntitySelectShell";
import { useAccumulatedList } from "./shared/useAccumulatedList";
import { renderDealOption } from "./DealOptionPreview";

interface DealSelectProps {
  value: string;
  onValueChange: (value: string, option?: SelectOption) => void;
  items?: DealListItem[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  isSearching?: boolean;
  onSearchChange?: (query: string) => void;
  searchTerm?: string;
  fullWidth?: boolean;
  expandInline?: boolean;
  expandInlineVariant?: "replace" | "floating";
  [key: string]: unknown;
}

const ADD_NEW_SENTINEL = "__ADD_NEW__";

export function DealSelectWithModal(props: DealSelectProps) {
  const {
    value,
    onValueChange,
    items,
    placeholder,
    disabled,
    isSearching,
    onSearchChange,
    searchTerm: searchTermProp,
    fullWidth,
    expandInline = true,
    expandInlineVariant = "floating",
  } = props;

  const {
    searchValue: dealSearchTerm,
    debouncedSearchValue: debouncedDealSearchTerm,
    setSearchValue: setDealSearchTerm,
  } = useDebouncedSearch({ initialValue: "", delay: 300 });

  const [page, setPage] = useState(1);
  const limit = 30;

  useEffect(() => {
    setPage(1);
  }, [debouncedDealSearchTerm]);

  const {
    data: dealsPage,
    isLoading: dealsLoading,
    isFetching: isBackgroundFetching,
  } = useDealsPaginated({
    page,
    limit,
    search: debouncedDealSearchTerm || undefined,
  });

  const currentDeals = useMemo<DealListItem[]>(
    () => dealsPage?.data ?? [],
    [dealsPage]
  );
  const accumulatedDeals = useAccumulatedList<DealListItem>({
    page: dealsPage?.page ?? page,
    firstPage: 1,
    items: currentDeals,
    idOf: (d) => String(d.id),
  });

  const globalModalStack = useGlobalModalStack();

  const buildOptions = useCallback(
    (deals: DealListItem[]): SelectOption[] =>
      deals
        .filter((deal) => deal?.id)
        .map((deal) => ({
          value: String(deal.id),
          label: deal.title || "Untitled",
          searchableText: [deal.title, deal.company_name, deal.stage]
            .filter(Boolean)
            .join(" "),
          company_name: deal.company_name,
          stage: deal.stage,
          revenue: deal.revenue,
        }) as SelectOption),
    []
  );

  const sourceDeals =
    accumulatedDeals.length > 0 ? accumulatedDeals : items || [];
  const options = useMemo(() => buildOptions(sourceDeals), [buildOptions, sourceDeals]);

  const isLoading =
    (typeof isSearching === "boolean" ? isSearching : undefined) ?? dealsLoading;

  const totalCount = dealsPage?.count ?? 0;
  const totalPages = Math.ceil(totalCount / limit);

  const handleLoadMore = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const handleAddNew = useCallback(() => {
    globalModalStack.openModal(MODAL_IDS.deals.createQuick);
  }, [globalModalStack]);

  const optionsWithAddNew = useMemo<SelectOption[]>(
    () => [...options, { value: ADD_NEW_SENTINEL, label: "Add New Deal" }],
    [options]
  );

  return (
    <>
      <div
        className={
          fullWidth
            ? "w-full flex items-center gap-2"
            : "flex items-center gap-2 w-full"
        }
      >
        <EntitySelectShell
          value={value}
          onValueChange={onValueChange}
          options={optionsWithAddNew}
          placeholder={placeholder}
          disabled={disabled}
          searchValue={dealSearchTerm}
          externalSearchValue={searchTermProp}
          setSearchValue={setDealSearchTerm}
          onSearchChange={onSearchChange}
          isLoading={!!isLoading}
          recentKey="deals"
          expandInline={expandInline}
          expandInlineVariant={expandInlineVariant}
          customOptionRenderer={renderDealOption}
          pageInfo={{
            page,
            totalPages,
            firstPage: 1,
            isBackgroundFetching: !!isBackgroundFetching,
            onLoadMore: handleLoadMore,
          }}
          addNewSentinel={ADD_NEW_SENTINEL}
          onAddNew={handleAddNew}
          containerClassName=""
          className="flex-1 min-w-0"
        />
      </div>
      <QuickDealCreationModal />
    </>
  );
}
